import type { TrackStyle } from './stylePresets.ts';

export function parseBackgroundColor(hex: string, opacity: number): string {
  const match = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!match) return hex;
  return `rgba(${Number.parseInt(match[1] ?? '0', 16)}, ${Number.parseInt(match[2] ?? '0', 16)}, ${Number.parseInt(match[3] ?? '0', 16)}, ${opacity})`;
}

export function textShadowFor(
  shadow: TrackStyle['shadow'],
  color: string,
): string | undefined {
  const shadows: Record<string, string> = {
    raised: `0 -1px 0 #fff, 0 1px 0 ${color}`,
    depressed: `0 -1px 0 ${color}, 0 1px 0 #fff`,
    outline: `-1px 1px 0 ${color}, 1px -1px 0 ${color}, 1px 1px 0 ${color}, -1px -1px 0 ${color}`,
    shadow: `1px 1px 1px ${color}`,
  };
  return shadow && shadows[shadow] ? shadows[shadow] : undefined;
}

export function boxShadowFor(boxShadow: TrackStyle['boxShadow']): string | undefined {
  const shadows: Record<string, string> = {
    soft: '0 2px 8px rgba(0, 0, 0, 0.45)',
    medium: '0 4px 14px rgba(0, 0, 0, 0.55)',
  };
  return boxShadow && shadows[boxShadow] ? shadows[boxShadow] : undefined;
}

export type PaintTrackStyleOptions = {
  viewHeight: number;
  /** Optional max-width in px; omit to leave unchanged. */
  maxWidthPx?: number;
  /** Class/style slot id used for Google font class names (main | second | preview-*). */
  googleSlot: string;
};

/** Apply visual track style onto a subtitle text element (inline styles). */
export function paintTrackStyle(
  element: HTMLElement,
  style: TrackStyle,
  options: PaintTrackStyleOptions,
): void {
  const fontSize = options.viewHeight * 0.045 * style.fontSizeRatio;
  element.style.fontSize = `${fontSize}px`;
  element.style.fontFamily = style.fontFamily;
  element.style.fontWeight = String(style.fontWeight);
  element.style.color = style.fontColor;
  element.style.letterSpacing = `${style.letterSpacing}em`;
  element.style.lineHeight = String(style.lineHeight);
  element.style.padding = `${style.padding}px`;
  element.style.opacity = String(style.opacity);
  element.style.backgroundColor = parseBackgroundColor(
    style.fontBackgroundColor,
    style.fontBackgroundColorOpacity,
  );
  if (options.maxWidthPx !== undefined) {
    element.style.maxWidth = `${options.maxWidthPx}px`;
  }

  if (style.strokeWidth) {
    element.style.webkitTextStrokeWidth = `${style.strokeWidth}px`;
    element.style.webkitTextStrokeColor = style.strokeColor;
    element.style.paintOrder = 'stroke fill';
  } else {
    element.style.removeProperty('-webkit-text-stroke-width');
    element.style.removeProperty('-webkit-text-stroke-color');
    element.style.removeProperty('paint-order');
  }

  const shadow = textShadowFor(style.shadow, style.shadowColor);
  if (shadow) {
    element.style.textShadow = shadow;
  } else {
    element.style.removeProperty('text-shadow');
  }

  const box = boxShadowFor(style.boxShadow);
  if (box) {
    element.style.boxShadow = box;
  } else {
    element.style.removeProperty('box-shadow');
  }

  applyGoogleFontClass(element, options.googleSlot, style);
}

export function applyGoogleFontClass(
  element: HTMLElement,
  slot: string,
  style: Pick<TrackStyle, 'fontGoogleFamilies' | 'fontGoogleCssProperties'>,
): void {
  const className = `unisubs-google-font-${slot}`;
  element.classList.remove(className);
  if (!style.fontGoogleFamilies || !style.fontGoogleCssProperties) return;
  element.style.removeProperty('font-family');
  element.style.removeProperty('font-weight');
  element.classList.add(className);
}

/**
 * Inject / replace a Google Fonts @import style tag for a slot.
 * Pass empty families/css to remove the tag.
 * Skips DOM work when the tag content is unchanged (avoids re-import thrash).
 */
export function syncGoogleFont(
  slot: string,
  families: string,
  cssProperties: string,
  doc: Pick<Document, 'getElementById' | 'createElement'> & {
    head: { appendChild: (node: Node) => Node };
  },
): void {
  const id = `unisubs-google-style-${slot}`;
  const existing = doc.getElementById(id);
  if (!families || !cssProperties) {
    existing?.remove();
    return;
  }

  const textContent =
    `@import url('https://fonts.googleapis.com/css2?${families}');\n` +
    `.unisubs-google-font-${slot} { ${cssProperties} }`;
  if (existing?.textContent === textContent) return;

  existing?.remove();
  const style = doc.createElement('style');
  style.id = id;
  style.textContent = textContent;
  doc.head.appendChild(style);
}
